// src/App.tsx
import React from 'react'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom'
import Login from './pages/Login'
import Projects from './pages/Projects'
import './App.scss'
import './styles/main.scss'
import TermCondition from './pages/TermCondition'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Layout from './components/layout/Layout'
import Profile from './pages/Profile'
import ErrorBoundary from './components/ErrorBoundary'
import ProjectOverview from './components/views/ProjectOverview/ProjectOverview'
import DatasetLabelingView from './components/views/DatasetLabelingView/DatasetLabelingView'
import ModelView from './components/views/ModelPerformanceView'
import AdminDashboard from './components/views/AdminDashboard/AdminDashboard'
import ModelComparisonView from './components/views/ModelComparisonView/ModelComparisonView'
import { useAppSelector } from './store/hooks'
import { selectIsAuthenticated } from './store/slices/authSlice'
import ModelsOverview from './components/views/ModelsOverview'
import ModelTrainingView from './components/views/ModelTrainingView/ModelTrainingView'
import InferenceLoop from './components/views/InferenceLoop/InferenceLoop'
import ReviewPhase from './components/views/InferenceLoop/components/ReviewPhase'
import DatasetOverview from './components/views/DatasetOverview/DatasetOverview'
import DatasetManageTags from './components/views/DatasetManageTags/DatasetManageTags'
import DatasetManageContent from './components/views/DatasetManageContent/DatasetManageContent'
import InferenceRealTimeSubView from './components/views/subviews/InferenceRealTimeSubView'
import InferenceOverviewSubView from './components/views/subviews/InferenceOverviewSubView'
import ModelDeploymentView from './components/views/ModelDeploymentView/ModelDeploymentView'
import SyntheticDataGeneration from './components/views/SyntheticGeneration'
import ModelDatasetsView from './components/views/ModelDatasetView'
import InferenceMapSubView from './components/views/subviews/InferenceMapSubView'
import ModelVersionsView from './components/views/ModelVersionsView'

const App: React.FC = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  // Add global navigation monitoring
  React.useEffect(() => {
    // Monitor navigation events
    const handleNavigation = () => {
      console.log('Navigation detected. Current URL:', window.location.href)
    }

    window.addEventListener('popstate', handleNavigation)

    // Override window.location.href setter
    try {
      const originalLocationDescriptor = Object.getOwnPropertyDescriptor(
        window.Location.prototype,
        'href'
      )
      if (originalLocationDescriptor && originalLocationDescriptor.set) {
        const originalSet = originalLocationDescriptor.set

        Object.defineProperty(window.location, 'href', {
          set: function (url) {
            return originalSet.call(this, url)
          },
          get: originalLocationDescriptor.get,
        })
      }
    } catch (e) {
      console.error('Failed to override location.href:', e)
    }

    return () => {
      window.removeEventListener('popstate', handleNavigation)
    }
  }, [])

  return (
    <Router>
      {/* Add the route debugger component */}
      <Layout>
        <Routes>
          <Route
            path="/docs/*"
            element={
              <ErrorBoundary>
                <div style={{ height: '100vh', width: '100vw' }}>
                  <iframe
                    title="Documentation"
                    src="/docs/intro/"
                    style={{ border: 'none', width: '100%', height: '100%' }}
                  />
                </div>
              </ErrorBoundary>
            }
          />
          <Route
            path="/login"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <Navigate to="/projects" /> : <Login />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Navigate to={isAuthenticated ? '/projects' : '/login'} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/projects"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <Projects /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/overview/:projectId"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ProjectOverview /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/overview/:projectId/overview"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ProjectOverview /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/datasets/:projectId/overview"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <DatasetOverview /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/datasets/:projectId/tags"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <DatasetManageTags /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/datasets/:projectId/labeling"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <DatasetLabelingView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/datasets/:projectId/content"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <DatasetManageContent /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/models/:projectId/overview"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ModelsOverview /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/models/:projectId/training"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ModelTrainingView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/models/:projectId/performance"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ModelView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/models/:projectId/deployment"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ModelDeploymentView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/models/:projectId/datasets"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ModelDatasetsView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/models/:projectId/compare"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ModelComparisonView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/models/:projectId/versions"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ModelVersionsView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/inference/:projectId/overview"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <InferenceOverviewSubView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/inference/:projectId/realtime"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <InferenceRealTimeSubView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/inference/:projectId/adaptive-active-labeling"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <InferenceLoop /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/inference/:projectId/map"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <InferenceMapSubView /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/inference/:projectId/synthetic-data"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <SyntheticDataGeneration /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/predictionsreview"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <ReviewPhase /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/profile"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <Profile /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
          <Route
            path="/termsconditions"
            element={
              <ErrorBoundary>
                <TermCondition />
              </ErrorBoundary>
            }
          />
          <Route
            path="/privacypolicy"
            element={
              <ErrorBoundary>
                <PrivacyPolicy />
              </ErrorBoundary>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <AdminDashboard /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />

          <Route
            path="/dashboard/users"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <AdminDashboard /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />

          <Route
            path="/dashboard/orgs"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <AdminDashboard /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />

          <Route
            path="/dashboard/manage"
            element={
              <ErrorBoundary>
                {isAuthenticated ? <AdminDashboard /> : <Navigate to="/login" />}
              </ErrorBoundary>
            }
          />
        </Routes>
      </Layout>
    </Router>
  )
}

export default App
